import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, Newspaper, User } from "lucide-react";
import api from "@/lib/api";

function Skeleton() {
  return (
    <div className="glass-panel rounded-2xl overflow-hidden animate-pulse">
      <div className="h-44 bg-white/5" />
      <div className="p-5 space-y-3">
        <div className="h-4 w-1/3 bg-white/10 rounded" />
        <div className="h-5 w-3/4 bg-white/10 rounded" />
        <div className="h-4 w-full bg-white/5 rounded" />
      </div>
    </div>
  );
}

function PostCard({ post, featured }) {
  const date = new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className={`group flex flex-col glass-panel rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-brand-cyan/30 ${
        featured ? "md:col-span-2 md:flex-row" : ""
      }`}
    >
      <div className={`relative overflow-hidden ${featured ? "md:w-1/2 h-56 md:h-auto" : "h-44"}`}>
        <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute top-3 left-3 rounded-full bg-brand-blue px-3 py-1 text-xs font-semibold text-white">{post.category}</span>
      </div>
      <div className={`flex flex-col p-6 ${featured ? "md:w-1/2 md:justify-center" : ""}`}>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {post.author}</span>
          <span>·</span>
          <span>{date}</span>
        </div>
        <h3 className={`mt-3 font-heading font-bold text-white group-hover:text-brand-cyan transition-colors ${featured ? "text-2xl" : "text-lg"}`}>
          {post.title}
        </h3>
        <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-3">{post.excerpt}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Clock className="h-3 w-3" /> {post.read_time} min read</span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-cyan">Read <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" /></span>
        </div>
      </div>
    </Link>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("All");

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch((e) => { console.error("Blog fetch failed:", e); setPosts([]); });
    api.get("/blog-categories").then((r) => setCats(r.data)).catch((e) => console.error("Categories fetch failed:", e));
  }, []);

  const filtered = posts && active !== "All" ? posts.filter((p) => p.category === active) : posts;

  return (
    <div data-testid="blog-page" className="pt-36 pb-24 min-h-screen bg-brand-slate">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-spotlight" />
        <div className="absolute inset-0 grid-overlay" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-cyan/25 bg-brand-cyan/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-cyan">
            <Newspaper className="h-3.5 w-3.5" /> Insights
          </span>
          <h1 className="mt-6 font-heading font-extrabold tracking-tighter text-white text-5xl sm:text-6xl">
            Real estate marketing, <span className="text-gradient">decoded</span>
          </h1>
          <p className="mt-5 text-lg text-slate-300 max-w-2xl mx-auto">
            Actionable playbooks on Meta Ads, WhatsApp automation and CRM systems that actually fill your pipeline.
          </p>
        </div>
      </section>

      <section className="relative mx-auto max-w-7xl px-5 sm:px-8 mt-12">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {["All", ...cats].map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                active === c ? "bg-brand-blue text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {!posts && Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} />)}
          {filtered && filtered.length === 0 && (
            <p className="col-span-full text-center text-slate-400 py-16">No articles yet — check back soon.</p>
          )}
          {filtered &&
            filtered.map((p, i) => <PostCard key={p.id} post={p} featured={i === 0 && active === "All"} />)}
        </div>
      </section>
    </div>
  );
}
